import iconCaretRight from '@phosphor-icons/core/regular/caret-right.svg';
import { Link, useNavigate, type LinkOptions } from '@tanstack/react-router';
import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';

import Counter from '@/components/ui/counter';
import Icon from '@/components/ui/icon';
import Toggle from '@/components/ui/toggle';
import { userTouching } from '@/utils/is-mobile';

type Menu = Array<MenuItem | null>;

type MenuItem = {
  action?: React.EventHandler<React.KeyboardEvent | React.MouseEvent>;
  active?: boolean;
  checked?: boolean;
  count?: number;
  destructive?: boolean;
  href?: string;
  icon?: string;
  meta?: string;
  middleClick?(event: React.MouseEvent): void;
  onChange?: (value: boolean) => void;
  target?: React.HTMLAttributeAnchorTarget;
  text: string;
  type?: 'toggle' | 'radio';
  items?: Menu;
  onSelectFile?: (files: FileList) => void;
  accept?: string;
  disabled?: boolean;
} & (LinkOptions | { to?: undefined });

interface IDropdownMenuItem {
  index: number;
  item: MenuItem | null;
  onClick?(goBack?: boolean): void;
  autoFocus?: boolean;
  onSetTab: (tab?: number) => void;
}

const DropdownMenuItem = ({ index, item, onClick, autoFocus, onSetTab }: IDropdownMenuItem) => {
  const navigate = useNavigate();

  const itemRef = useRef<HTMLAnchorElement>(null);
  const fileElement = useRef<HTMLInputElement>(null);

  const handleClick: React.EventHandler<React.MouseEvent | React.KeyboardEvent> = (event) => {
    event.stopPropagation();

    if (!item) return;
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    if (item.items?.length) {
      event.preventDefault();
      onSetTab(index);
      return;
    }

    if (item.onSelectFile) {
      fileElement.current?.click();
      return;
    }

    if (onClick) onClick(!(item.to && userTouching.matches));

    if (item.to) {
      event.preventDefault();
      if (userTouching.matches) {
        navigate({ to: item.to, params: item.params, search: item.search, replace: true });
      } else navigate({ to: item.to, params: item.params, search: item.search });
    } else if (typeof item.action === 'function') {
      event.preventDefault();
      item.action(event);
    } else if (typeof item.onChange === 'function') {
      event.preventDefault();
      item.onChange(!item.checked);
    }
  };

  const handleAuxClick: React.EventHandler<React.MouseEvent> = (event) => {
    if (!item) return;
    if (item.disabled) return;
    if (item.onSelectFile) fileElement.current?.click();
    if (onClick) onClick();

    if (event.button === 1 && item.middleClick) {
      item.middleClick(event);
    }
  };

  const handleItemKeyDown: React.KeyboardEventHandler<HTMLAnchorElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick(event);
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!item) return;
    if (item.disabled) return;

    if (item.onChange) item.onChange(event.target.checked);
  };

  const handleSelectFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.target.files?.length && item?.onSelectFile) {
      item.onSelectFile(e.target.files);
    }
  };

  useEffect(() => {
    const firstItem = index === 0;

    if (itemRef.current && (autoFocus ? firstItem : item?.active)) {
      itemRef.current.focus({ preventScroll: true });
    }
  }, [index]);

  if (item === null) {
    return <hr />;
  }

  const itemContent = (
    <>
      {item.icon && <Icon src={item.icon} className='⁂-dropdown-menu__item__icon' />}

      <div className='⁂-dropdown-menu__item__content'>
        {item.meta ? (
          <>
            <div>{item.text}</div>
            <div>{item.meta}</div>
          </>
        ) : (
          item.text
        )}
      </div>

      {item.count ? (
        <span className='⁂-dropdown-menu__item__counter'>
          <Counter count={item.count} />
        </span>
      ) : null}

      {(item.type === 'toggle' || item.type === 'radio') && (
        <div className='⁂-dropdown-menu__item__switch'>
          <Toggle
            checked={item.checked}
            onChange={handleChange}
            radio={item.type === 'radio'}
            disabled={item.disabled}
          />
        </div>
      )}

      {!!item.items?.length && (
        <Icon src={iconCaretRight} containerClassName='⁂-dropdown-menu__item__expand' />
      )}
    </>
  );

  const itemProps = {
    tabIndex: item.disabled ? -1 : 0,
    ref: itemRef,
    'data-index': index,
    onClick: handleClick,
    onAuxClick: handleAuxClick,
    onKeyDown: handleItemKeyDown,
    title: item.text,
    'aria-disabled': item.disabled,
    className: clsx('⁂-dropdown-menu__item', {
      '⁂-dropdown-menu__item--destructive': item.destructive,
    }),
  };

  return (
    <li>
      {item.to ? (
        <Link
          to={item.to}
          params={item.params}
          search={item.search}
          target={typeof item.target === 'string' ? item.target : undefined}
          {...itemProps}
        >
          {itemContent}
        </Link>
      ) : (
        <a
          href={item.href ?? '#'}
          target={typeof item.target === 'string' ? item.target : '_blank'}
          {...itemProps}
        >
          {itemContent}
        </a>
      )}

      {item.onSelectFile && (
        <label className='sr-only'>
          <span>{item.text}</span>
          <input
            ref={fileElement}
            type='file'
            accept={item.accept}
            onChange={handleSelectFileChange}
            className='hidden'
            disabled={item.disabled}
          />
        </label>
      )}
    </li>
  );
};

export { type Menu, type MenuItem, DropdownMenuItem as default };
